import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { can } from '../../utils/permissions';
import { saveText } from '../../utils/download';
import { errorText } from '../../utils/errorText';
import { badge, box, button, input, label } from '../shared/styles';
import AnalysisView from './AnalysisView';
import PlatesPanel from './PlatesPanel';
import ReportView from './ReportView';
import CultureTab from './CultureTab';
import ThermalTab from './ThermalTab';

/*
 * One experiment through its life: planned -> started under a signed protocol
 * version -> values entered by the lab (never edited: corrected with a
 * reason) -> completed -> analysed, only then, on what was entered.
 *
 * Three tabs, the one shown held in the address's ?tab=: Overview, all of the
 * above; Thermal & Metabolic (ThermalTab.js, phase C3) and Culture
 * (CultureTab.js, migration 0104: the units' plates' dish readings, recorded
 * as observations), both for lab readers.
 * A finished experiment can be archived -- filed away from the History list --
 * by its owner, a collection manager or a system admin.
 */
export const TABS = ['overview', 'thermal', 'culture'];

/** The tab the address names, else Overview. */
export function tabFromAddress(search = (typeof window !== 'undefined' ? window.location.search : '')) {
  const tab = new URLSearchParams(search).get('tab');
  return TABS.includes(tab) ? tab : 'overview';
}

/** Keep ?tab= in the address without a navigation: Back leaves the page, not the tab. */
function writeTab(tab) {
  try {
    const url = new URL(window.location.href);
    if (tab === 'overview') url.searchParams.delete('tab');
    else url.searchParams.set('tab', tab);
    window.history.replaceState(window.history.state, '', url);
  } catch (_) { /* no address to keep it in */ }
}
function StartPanel({ detail, onDone, t }) {
  const [protocols, setProtocols] = useState([]);
  const [protocolId, setProtocolId] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    api.get('/protocols').then(({ data }) => setProtocols(data.filter((p) => p.current_version > 0)))
      .catch((e) => setError(errorText(e)));
  }, []);
  const chosen = protocols.find((p) => p.protocol_id === protocolId);
  const start = async () => {
    setError('');
    try {
      await api.post(`/experiments/${detail.experiment_id}/start`,
        { protocol_id: protocolId, protocol_version: chosen?.current_version });
      onDone();
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <section style={box}>
      <h4 style={{ marginTop: 0 }}>{t('start')}</h4>
      <p style={{ fontSize: 13 }}>{t('startIntro')}</p>
      <label style={label}>{t('protocol')}
        <select style={input} value={protocolId} onChange={(e) => setProtocolId(e.target.value)} title={t('tip.startProtocol')} data-testid="start-protocol">
          <option value="">—</option>
          {protocols.map((p) => <option key={p.protocol_id} value={p.protocol_id}>{p.name} · v{p.current_version}</option>)}
        </select>
      </label>
      <button type="button" style={button('var(--c-primary)')} disabled={!chosen} onClick={start} title={t('tip.start')} data-testid="start">
        {t('startButton')}
      </button>
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
    </section>
  );
}

function DataEntry({ detail, onSaved, t }) {
  const repeated = detail.design_kind === 'repeated';
  const [timepoint, setTimepoint] = useState('');
  const [values, setValues] = useState({});
  const [notes, setNotes] = useState({});
  const [error, setError] = useState('');
  const cell = (u, o) => `${u}|${o}`;
  const save = async () => {
    setError('');
    // A cell with a value, or with only a note: a note alone records the
    // value as missing and says why ("the flask cracked").
    const observations = detail.units.flatMap((u) => detail.outcomes.map((o) => {
      const raw = values[cell(u.unit_id, o.key)] ?? '';
      const note = (notes[cell(u.unit_id, o.key)] ?? '').trim();
      if (raw === '' && !note) return null;
      return { unit_id: u.unit_id, outcome: o.key, value: raw === '' ? null : Number(raw),
        ...(note ? { note } : {}), ...(timepoint ? { timepoint } : {}) };
    })).filter(Boolean);
    if (!observations.length) return;
    try {
      await api.post(`/experiments/${detail.experiment_id}/observations`, { observations });
      setValues({});
      setNotes({});
      onSaved();
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <section style={box} data-testid="data-entry">
      <h4 style={{ marginTop: 0 }}>{t('enterValues')}</h4>
      <p style={{ fontSize: 13 }}>{t('enterIntro')}</p>
      {(repeated || detail.observation_count > 0) && (
        <label style={label}>{t('timepoint')}{repeated ? ' *' : ''}
          <input style={input} value={timepoint} placeholder="d7" onChange={(e) => setTimepoint(e.target.value)}
            title={t('tip.timepoint')} data-testid="timepoint" />
        </label>
      )}
      <table style={{ fontSize: 13, borderCollapse: 'collapse' }}>
        <thead><tr><th style={{ textAlign: 'start' }}>{t('unit')}</th>
          {detail.outcomes.map((o) => <th key={o.key}>{o.label}{o.unit ? ` (${o.unit})` : ''}</th>)}</tr></thead>
        <tbody>{detail.units.map((u) => (
          <tr key={u.unit_id}><td>{u.label}</td>
            {detail.outcomes.map((o) => (
              <td key={o.key}><input style={{ ...input, width: 90 }} inputMode="decimal" title={t('tip.value')}
                value={values[cell(u.unit_id, o.key)] ?? ''} data-testid={`value-${u.arm}-${u.replicate}-${o.key}`}
                onChange={(e) => setValues({ ...values, [cell(u.unit_id, o.key)]: e.target.value })} />
                <input style={{ ...input, width: 90, display: 'block', marginTop: 2, fontSize: 12 }} placeholder={t('note')}
                  title={t('tip.note')} value={notes[cell(u.unit_id, o.key)] ?? ''} maxLength={2000}
                  data-testid={`note-${u.arm}-${u.replicate}-${o.key}`}
                  onChange={(e) => setNotes({ ...notes, [cell(u.unit_id, o.key)]: e.target.value })} /></td>
            ))}</tr>
        ))}</tbody>
      </table>
      <button type="button" style={{ ...button('var(--c-primary)'), marginTop: 8 }} onClick={save} title={t('tip.saveValues')} data-testid="save-values">
        {t('saveValues')}
      </button>
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
    </section>
  );
}

function Observations({ detail, canCorrect, reloadKey, onChanged, t }) {
  const [rows, setRows] = useState([]);
  const [fixing, setFixing] = useState(null);
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    api.get(`/experiments/${detail.experiment_id}/observations`).then(({ data }) => setRows(data))
      .catch((e) => setError(errorText(e)));
  }, [detail.experiment_id, reloadKey]);
  // The note goes with the corrected value: it starts as the old one, and
  // what is in the field when saving is what the correction carries.
  const open = (o) => {
    setFixing(o.observation_id);
    setNote(o.note || '');
  };
  const correct = async () => {
    setError('');
    try {
      await api.post(`/experiments/${detail.experiment_id}/observations/${fixing}/correct`,
        { value: value === '' ? null : Number(value), reason, note: note.trim() || null });
      setFixing(null);
      setValue('');
      setReason('');
      setNote('');
      onChanged();
    } catch (e) {
      setError(errorText(e));
    }
  };
  if (!rows.length) return null;
  return (
    <section style={box}>
      <h4 style={{ marginTop: 0 }}>{t('observations', { count: rows.length })}</h4>
      <table style={{ fontSize: 13, width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>{['unit', 'outcome', 'timepoint', 'value', 'note', 'enteredBy'].map((c) => <th key={c} style={{ textAlign: 'start' }}>{t(c)}</th>)}<th /></tr></thead>
        <tbody>{rows.map((o) => (
          <tr key={o.observation_id}>
            <td>{o.unit_label}</td><td>{o.outcome}</td><td>{o.timepoint || ''}</td>
            <td>{o.value ?? '—'}{o.replaces && <span style={badge('warning')} title={o.reason}>{t('corrected')}</span>}</td>
            <td data-testid={`obs-note-${o.observation_id}`}>{o.note || ''}</td>
            <td>{o.entered_by_username}</td>
            <td>{canCorrect && (
              <button type="button" onClick={() => open(o)} title={t('tip.correct')} data-testid={`correct-${o.observation_id}`}>
                {t('correct')}</button>)}</td>
          </tr>
        ))}</tbody>
      </table>
      {fixing && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          <input style={input} placeholder={t('value')} value={value} onChange={(e) => setValue(e.target.value)}
            title={t('tip.correctValue')} data-testid="correct-value" />
          <input style={{ ...input, flex: 1 }} placeholder={t('correctionReason')} value={reason}
            onChange={(e) => setReason(e.target.value)} title={t('tip.correctReason')} data-testid="correct-reason" />
          <input style={{ ...input, flex: 1 }} placeholder={t('note')} value={note} maxLength={2000}
            onChange={(e) => setNote(e.target.value)} title={t('tip.correctNote')} data-testid="correct-note" />
          <button type="button" onClick={correct} title={t('tip.saveCorrection')} data-testid="correct-save">{t('saveCorrection')}</button>
        </div>
      )}
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
    </section>
  );
}

export default function ExperimentDetail({ experimentId, onChanged }) {
  const { t } = useTranslation('experiments');
  const { user } = useAuth();
  const [detail, setDetail] = useState(null);
  const [reports, setReports] = useState([]);
  const [analysisId, setAnalysisId] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [error, setError] = useState('');
  const canWrite = can(user, 'experiment:write');
  const canObserve = can(user, 'lab:observe');
  const thermalReader = can(user, 'lab:read');
  const [tab, setTab] = useState(() => (thermalReader ? tabFromAddress() : 'overview'));
  const choose = (next) => {
    setTab(next);
    writeTab(next);
  };
  const tabKeys = (e) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    const next = e.key === 'Home' ? TABS[0] : e.key === 'End' ? TABS[TABS.length - 1]
      : TABS[(TABS.indexOf(tab) + 1) % TABS.length];
    choose(next);
    document.getElementById(`experiment-tab-${next}`)?.focus();
  };

  const load = useCallback(async () => {
    try {
      const [{ data }, { data: reps }] = await Promise.all([
        api.get(`/experiments/${experimentId}`), api.get(`/experiments/${experimentId}/reports`)]);
      setDetail(data);
      setReports(reps);
      setReloadKey((k) => k + 1);
    } catch (e) {
      setError(errorText(e));
    }
  }, [experimentId]);
  useEffect(() => { load(); }, [load]);

  const post = async (path, body) => {
    setError('');
    try {
      const { data } = await api.post(`/experiments/${experimentId}${path}`, body);
      await load();
      onChanged?.();
      return data;
    } catch (e) {
      setError(errorText(e));
      return null;
    }
  };

  // The whole experiment as one document (GET /experiments/{id}/record):
  // Markdown for the lab notebook, or the JSON it is made from. Each
  // download is a row in the audit ledger.
  const downloadRecord = async (format) => {
    setError('');
    try {
      const { data } = await api.get(`/experiments/${experimentId}/record`, { params: { format } });
      if (format === 'json') saveText(JSON.stringify(data, null, 2), `${detail.code}-record.json`, 'application/json');
      else saveText(data.content, data.filename, data.media_type);
    } catch (e) {
      setError(errorText(e));
    }
  };

  if (!detail) return error ? <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p> : <p>{t('loading')}</p>;
  return (
    <div data-testid="experiment-detail">
      <h3 style={{ marginTop: 0 }}>{detail.code} · {detail.title}
        <span style={badge(detail.status === 'completed' ? 'success' : 'info')} data-testid="exp-status">
          {t(`status_${detail.status}`)}</span>
        {detail.archived_at && <span style={badge('warning')} data-testid="exp-archived">{t('archived')}</span>}</h3>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', fontSize: 13, marginBottom: 8 }}
        role="group" aria-label={t('downloadRecord')}>
        <span>{t('downloadRecord')}:</span>
        <button type="button" onClick={() => downloadRecord('md')} title={t('tip.recordMarkdown')}
          data-testid="record-md">{t('recordMarkdown')}</button>
        <button type="button" onClick={() => downloadRecord('json')} title={t('tip.recordJson')}
          data-testid="record-json">{t('recordJson')}</button>
        {canWrite && detail.may_manage && ['completed', 'abandoned'].includes(detail.status) && (
          detail.archived_at
            ? <button type="button" onClick={() => post('/unarchive', {})} title={t('tip.unarchive')}
              data-testid="unarchive">{t('unarchive')}</button>
            : <button type="button" onClick={() => post('/archive', {})} title={t('tip.archive')}
              data-testid="archive">{t('archive')}</button>
        )}
      </div>
      {thermalReader && (
        <div role="tablist" aria-label={t('tabs.label')} onKeyDown={tabKeys}
          style={{ display: 'flex', gap: 4, borderBlockEnd: '1px solid var(--c-border)', marginBottom: 12 }}>
          {TABS.map((name) => (
            <button key={name} type="button" role="tab" id={`experiment-tab-${name}`} aria-selected={tab === name}
              aria-controls={`experiment-panel-${name}`} tabIndex={tab === name ? 0 : -1}
              onClick={() => choose(name)} data-testid={`tab-${name}`}
              style={{ background: 'none', border: 'none', padding: '6px 12px', cursor: 'pointer', fontSize: 14,
                color: 'var(--c-text-fg)', fontWeight: tab === name ? 700 : 400,
                borderBlockEnd: `3px solid ${tab === name ? 'var(--c-primary)' : 'transparent'}` }}>
              {t(`tabs.${name}`)}
            </button>
          ))}
        </div>
      )}
      {tab === 'thermal' && (
        <div role="tabpanel" id="experiment-panel-thermal" aria-labelledby="experiment-tab-thermal">
          <ThermalTab experimentId={experimentId} user={user} onChanged={onChanged} />
        </div>
      )}
      {tab === 'culture' && (
        <div role="tabpanel" id="experiment-panel-culture" aria-labelledby="experiment-tab-culture">
          <CultureTab experimentId={experimentId} onChanged={onChanged} />
        </div>
      )}
      {tab === 'overview' && (
      <div role={thermalReader ? 'tabpanel' : undefined} id="experiment-panel-overview"
        aria-labelledby={thermalReader ? 'experiment-tab-overview' : undefined}>
      {detail.hypothesis && <p><em>{detail.hypothesis}</em></p>}
      <p style={{ fontSize: 13 }}>
        {t(`kind_${detail.design_kind}`)} · {detail.arms.map((a) => `${a.key} ×${a.replicates}`).join(', ')}
        {detail.protocol_name && <> · {t('protocol')}: {detail.protocol_name} v{detail.protocol_version}</>}
      </p>
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}

      {detail.status === 'planned' && canWrite && <StartPanel detail={detail} onDone={load} t={t} />}
      {detail.units.length > 0 && (
        <PlatesPanel detail={detail} running={detail.status === 'running'} canWrite={canWrite}
          canObserve={canObserve} onChanged={load} t={t} />
      )}
      {detail.status === 'running' && canObserve && <DataEntry detail={detail} onSaved={load} t={t} />}
      <Observations detail={detail} reloadKey={reloadKey} onChanged={load} t={t}
        canCorrect={canObserve && ['running', 'completed'].includes(detail.status)} />
      {detail.status === 'running' && canWrite && (
        <button type="button" style={button('var(--c-primary)')} data-testid="complete" title={t('tip.complete')}
          onClick={() => post('/complete', {})}>{t('complete')}</button>
      )}

      {detail.status === 'completed' && (
        <section style={box}>
          <h4 style={{ marginTop: 0 }}>{t('analyses')}</h4>
          <p style={{ fontSize: 13 }}>{t('analysisIntro')}</p>
          {canWrite && (
            <button type="button" style={button('var(--c-primary)')} data-testid="run-analysis" title={t('tip.runAnalysis')}
              onClick={async () => { const a = await post('/analyses', {}); if (a) setAnalysisId(a.analysis_id); }}>
              {t('runAnalysis')}</button>
          )}
          <ul>{detail.analyses.map((a) => (
            <li key={a.analysis_id}>
              <button type="button" onClick={() => { setAnalysisId(a.analysis_id); setReportId(null); }}
                title={t('tip.openAnalysis')} data-testid={`analysis-${a.analysis_id}`}>
                {new Date(a.created_at).toLocaleString()} · {t('planVersion')} {a.plan_version} · {t(`analysis_${a.status}`)}
              </button>
            </li>
          ))}</ul>
        </section>
      )}
      {analysisId && (
        <AnalysisView key={analysisId} experimentId={experimentId} analysisId={analysisId}
          canSubmit={can(user, 'research:submit')}
          onReport={(rid) => { setReportId(rid); load(); }} />
      )}
      {reports.length > 0 && (
        <section style={box}>
          <h4 style={{ marginTop: 0 }}>{t('reports')}</h4>
          <ul>{reports.map((r) => (
            <li key={r.report_id}><button type="button" onClick={() => setReportId(r.report_id)} title={t('tip.openReport')}
              data-testid={`report-${r.report_id}`}>{new Date(r.created_at).toLocaleString()} · {t(`status_${r.status}`)}</button></li>
          ))}</ul>
        </section>
      )}
      {reportId && <ReportView key={reportId} experimentId={experimentId} reportId={reportId} />}
      </div>
      )}
    </div>
  );
}
